import { useEffect, type RefObject } from "react";

interface UseRevealOnScrollOptions {
  /** 관찰 대상 selector (기본 `.reveal-fade, .reveal-slide`) */
  selector?: string;
  /** IntersectionObserver threshold (기본 0) */
  threshold?: number;
  /** IntersectionObserver rootMargin (기본 `0px 0px -40px 0px`) */
  rootMargin?: string;
  /** observer 시작 지연 (ms). 초기 렌더에 opacity:0 을 확실히 반영할 때 사용 */
  startDelay?: number;
  /** 값이 바뀌면 observer 를 재시작 (예: locale 변경으로 컨텐츠 재렌더) */
  resetKey?: unknown;
}

/**
 * scope 내부의 reveal 대상이 뷰포트에 들어오면 `.is-visible` 클래스를 부여.
 * `scopeRef` 가 null 이면 document 전체를 관찰.
 */
export function useRevealOnScroll(
  scopeRef: RefObject<HTMLElement | null> | null,
  {
    selector = ".reveal-fade, .reveal-slide",
    threshold = 0,
    rootMargin = "0px 0px -40px 0px",
    startDelay = 0,
    resetKey,
  }: UseRevealOnScrollOptions = {},
) {
  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    const start = () => {
      const root: Document | HTMLElement = scopeRef?.current ?? document;
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              observer?.unobserve(entry.target);
            }
          });
        },
        { threshold, rootMargin },
      );
      const targets = root.querySelectorAll(selector);
      targets.forEach((el) => observer!.observe(el));
    };

    const timer = startDelay > 0 ? window.setTimeout(start, startDelay) : null;
    if (timer === null) start();

    return () => {
      if (timer !== null) window.clearTimeout(timer);
      observer?.disconnect();
    };
  }, [scopeRef, selector, threshold, rootMargin, startDelay, resetKey]);
}
